import React, { useState, useEffect } from 'react'
import Layout from '@/components/Layout'
import { getAllBlogPosts } from '@/lib/api'
import { BlogPost } from '@/types'
import Link from 'next/link'
import { urlFor } from '@/lib/sanity'
import SocialLinks from '@/components/ui/SocialLinks'
import BlockchainProfiles from '@/components/ui/BlockchainProfiles'
import { formatDate } from '@/lib/utils'

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [categories, setCategories] = useState<string[]>([])

  // Fetch blog posts
  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const posts = await getAllBlogPosts()
        setBlogPosts(posts)

        // Extract unique categories
        const allCategories = posts.flatMap(post => post.categories || [])
        const uniqueCategories = Array.from(new Set(allCategories))
        setCategories(uniqueCategories)

        setIsLoading(false)
      } catch (error) {
        console.error('Failed to fetch blog posts:', error)
        setIsLoading(false)
      }
    }

    fetchBlogPosts()
  }, [])

  // Filter posts by category
  const filteredPosts = selectedCategory
    ? blogPosts.filter(post => post.categories?.includes(selectedCategory))
    : blogPosts

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-4 gradient-text">Blog</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Insights and articles about blockchain development, web3, and related technologies.
          </p>
        </div>

        {/* Social Media Links Section */}
        <div className="mb-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold mb-4">Connect & Follow</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Stay updated with my latest articles, projects, and blockchain insights by following me
            on social media.
          </p>

          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <h3 className="text-lg font-medium mb-3">Social Media</h3>
              <SocialLinks showLabels iconSize="md" className="flex flex-col gap-3" />
            </div>

            <div className="flex-1">
              <h3 className="text-lg font-medium mb-3">Blockchain Profiles</h3>
              <BlockchainProfiles iconSize="md" className="flex flex-wrap gap-2" />
            </div>
          </div>
        </div>

        {/* Category Filter */}
        {categories.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === null
                    ? 'bg-primary dark:bg-primary-light text-white dark:text-gray-900'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                All
              </button>
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary dark:bg-primary-light text-white dark:text-gray-900'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Blog Posts */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredPosts.map(post => (
              <Link href={`/blog/${post.slug.current}`} key={post._id}>
                <div className="group bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300">
                  {post.featuredImage && (
                    <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                      <img
                        src={urlFor(post.featuredImage).width(600).height(340).url()}
                        alt={post.featuredImage.alt || post.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex flex-wrap gap-2 mb-3">
                      {post.categories?.map(category => (
                        <span
                          key={category}
                          className="inline-block px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                    <h2 className="text-xl font-bold mb-2 group-hover:text-primary dark:group-hover:text-primary-light transition-colors">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(post.publishedAt)}
                      </span>
                      <span className="text-primary dark:text-primary-light font-medium text-sm">
                        Read more →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-medium mb-2">No blog posts found</h3>
            <p className="text-gray-600 dark:text-gray-300">
              {selectedCategory
                ? `No posts found in the "${selectedCategory}" category.`
                : 'Check back later for new articles.'}
            </p>
          </div>
        )}
      </div>
    </>
  )
}

export default BlogPage
